import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HomeStructuredData } from './SEO/StructuredData';
import {
  SparklesIcon,
  ArrowRightIcon,
  StarIcon,
  CodeBracketIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  CpuChipIcon,
  CommandLineIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  Squares2X2Icon,
  FireIcon,
  TagIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { products } from '../data/products';
import ProductCard from './ProductCard';
import SearchResults from './SearchResults';
import { searchWebsite } from '../utils/search';
import { toast } from 'react-hot-toast';

const Home = ({ activeUsers }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isSellClicked, setIsSellClicked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState('');

  useEffect(() => {
    const examples = [
      'resume templates',
      'SaaS playbooks',
      'side hustle playbooks',
      'social media workbooks',
      'planners'
    ];
    let exampleIndex = 0;
    let characterIndex = 0;
    let isDeleting = false;
    let placeholderTimer;

    const animatePlaceholder = () => {
      const currentExample = examples[exampleIndex];

      if (isDeleting) {
        characterIndex -= 1;
      } else {
        characterIndex += 1;
      }

      setSearchPlaceholder(currentExample.slice(0, characterIndex));

      if (!isDeleting && characterIndex === currentExample.length) {
        isDeleting = true;
        placeholderTimer = setTimeout(animatePlaceholder, 1100);
        return;
      }

      if (isDeleting && characterIndex === 0) {
        isDeleting = false;
        exampleIndex = (exampleIndex + 1) % examples.length;
        placeholderTimer = setTimeout(animatePlaceholder, 350);
        return;
      }

      placeholderTimer = setTimeout(animatePlaceholder, isDeleting ? 45 : 85);
    };

    placeholderTimer = setTimeout(animatePlaceholder, 85);

    return () => clearTimeout(placeholderTimer);
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) return;

    setSearchResults(searchWebsite(trimmedQuery));
    setShowSearchResults(true);
  };

  const handleSearchChange = (event) => {
    const nextQuery = event.target.value;
    setSearchQuery(nextQuery);

    if (nextQuery.trim().length >= 2) {
      setSearchResults(searchWebsite(nextQuery));
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = () => {
    setShowSearchResults(false);
    setSearchQuery('');
  };

  const handleSellClick = () => {
    setIsSellClicked(true);
    toast('Seller Portal Coming Soon!', {
      icon: '🚀',
      style: {
        background: '#9FE240',
        color: '#000000',
        border: '2px solid #000000',
        boxShadow: '4px 4px 0px 0px #000000',
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }
    });
    setTimeout(() => {
      setIsSellClicked(false);
    }, 2500);
  };

  // Asset categories for marketplace browsing
  const assetCategories = [
    {
      title: 'Python Automation',
      count: '700+ Tools & Scripts',
      description: 'Web scrapers, bots, task automation, and CLI utilities ready to deploy.',
      icon: CodeBracketIcon,
      link: '/products?category=Python%20Development',
      tag: 'HOT'
    },
    {
      title: 'Cybersecurity & SOC',
      count: '40+ Toolkits & Labs',
      description: 'Threat hunting playbooks, penetration testbeds, and security concepts.',
      icon: ShieldCheckIcon,
      link: '/products?category=Cybersecurity%20Training',
      tag: 'POPULAR'
    },
    {
      title: 'AI & Machine Learning',
      count: '15+ Handbooks & Notebooks',
      description: 'Jupyter notebooks, deep learning guides, and applied ML algorithms.',
      icon: CpuChipIcon,
      link: '/products?category=AI%20%26%20Machine%20Learning',
      tag: 'NEW'
    },
    {
      title: 'DevOps & Systems',
      count: '30+ Configs & Pipelines',
      description: 'CI/CD workflows, Docker configs, server administration scripts.',
      icon: CommandLineIcon,
      link: '/products?category=DevOps%20Tools',
      tag: 'ESSENTIAL'
    },
    {
      title: 'Business & Legal Docs',
      count: '120+ Verified Templates',
      description: 'Operating agreements, pitch decks, contracts, and financial models.',
      icon: DocumentTextIcon,
      link: '/products?category=Legal%20Documents',
      tag: 'READY'
    },
    {
      title: 'Digital Mega Bundles',
      count: 'Complete Asset Suites',
      description: 'All-inclusive multi-resource bundles with huge savings and bonus packs.',
      icon: Squares2X2Icon,
      link: '/digital-bundles',
      tag: '50% OFF'
    }
  ];

  // Curated products for different marketplace tabs
  const trendingProductIds = [1, 2, 4, 3, 5, 7];
  const devProductIds = [1, 2, 4, 5];
  const securityProductIds = [3, 1, 2, 4];

  const displayedProducts = activeTab === 'all'
    ? products.filter(p => trendingProductIds.includes(p.id))
    : activeTab === 'dev'
    ? products.filter(p => devProductIds.includes(p.id))
    : products.filter(p => securityProductIds.includes(p.id));

  // Marketplace quick tags for the hero
  const heroQuickTags = [
    { label: 'Automation Scripts', link: '/products?category=Python%20Development' },
    { label: 'AI Handbooks', link: '/products?category=AI%20%26%20Machine%20Learning' },
    { label: 'SOC Toolkits', link: '/products?category=Cybersecurity%20Training' },
    { label: 'Business Templates', link: '/products?category=Business%20Planning' },
    { label: 'Mega Bundles', link: '/digital-bundles' }
  ];

  return (
    <>
      <Helmet>
        <title>Pingfence | Digital Asset Marketplace & Developer Library</title>
        <meta 
          name="description" 
          content="Discover India's premier digital asset marketplace. Instant download for production-grade Python automation tools, cybersecurity frameworks, AI handbooks, and business templates." 
        />
      </Helmet>
      <HomeStructuredData />

      <div className="space-y-12 sm:space-y-18 lg:space-y-24">

        {/* 1. HERO SECTION: LEAN, SPACIOUS & IMPACTFUL DIGITAL ASSET MARKETPLACE */}
        <section className="relative pt-16 sm:pt-[10vh] pb-10 sm:pb-16 lg:pb-24 border-b-2 sm:border-b-4 border-black bg-transparent">
          <div className="container relative z-10">
            <div className="max-w-6xl mx-auto text-center space-y-4 sm:space-y-6 lg:space-y-8">

              {activeUsers !== null && (
                <div className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-black uppercase tracking-wide text-black shadow-[3px_3px_0px_0px_#000000]">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
                  <span>{activeUsers} {activeUsers === 1 ? 'person' : 'people'} online now</span>
                </div>
              )}

              {/* Main Headline */}
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black uppercase tracking-tight text-black leading-[1.08] sm:leading-none" 
                style={{ textShadow: '2px 2px 0px #9FE240' }}
              >
                Buy & Deploy Pro <span className="bg-primary text-black px-2 sm:px-4 py-0.5 sm:py-1 border-2 sm:border-4 border-black inline-block rotate-[-0.5deg] sm:rotate-[-1deg] shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] my-1 sm:my-0">Digital Assets</span>
              </h1>

              {/* Breathable Subtitle */}
              <p className="text-black text-sm sm:text-lg lg:text-xl font-medium sm:font-bold max-w-3xl mx-auto leading-relaxed px-2 sm:px-0 text-gray-800">
                500+ production-grade automation scripts, developer toolkits, cybersecurity frameworks, and AI repositories. Instant cloud delivery with perpetual commercial rights.
              </p>

              {/* Primary actions */}
              <div className="flex flex-col items-center gap-3.5 pt-3 sm:pt-4 max-w-2xl mx-auto w-full mb-8 sm:mb-[10vh]">
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex w-full items-stretch border-4 border-black bg-white shadow-[4px_4px_0px_0px_#000000]"
                >
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={searchPlaceholder}
                    aria-label="Search digital assets"
                    className="min-w-0 flex-1 bg-transparent px-4 py-3.5 text-base font-bold text-black placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-primary sm:px-5 sm:py-4 sm:text-lg"
                  />
                  <button
                    type="submit"
                    aria-label="Search"
                    className="flex shrink-0 items-center justify-center border-l-4 border-black bg-primary px-2.5 py-3 text-sm font-black uppercase text-black transition-colors hover:bg-primary-light sm:px-4 sm:py-3.5 sm:text-base"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5 text-black sm:h-6 sm:w-6" />
                  </button>
                </form>
                <button
                  type="button"
                  onClick={handleSellClick}
                  className="btn bg-black text-primary hover:bg-black/90 w-full max-w-sm text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 font-black flex items-center justify-center gap-2 border-2 border-black shadow-[3px_3px_0px_0px_#9FE240] transition-all cursor-pointer"
                >
                  {isSellClicked ? (
                    <>
                      <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary animate-pulse" />
                      <span>Coming Soon!</span>
                    </>
                  ) : (
                    <>
                      <TagIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      <span>Sell Your Assets</span>
                    </>
                  )}
                </button>
              </div>

              {/* Popular quick tags */}
              <div className="pt-2 sm:pt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 px-2">
                <span className="w-full sm:w-auto text-xs font-black uppercase text-gray-500 tracking-wider mb-1 sm:mb-0">Trending:</span>
                {heroQuickTags.map((tag, idx) => (
                  <Link
                    key={idx}
                    to={tag.link}
                    className="text-xs md:text-sm font-bold uppercase px-3 py-1.5 sm:px-3.5 sm:py-1.5 bg-white hover:bg-primary border border-black sm:border-2 text-black shadow-[2px_2px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000] hover:-translate-y-0.5 transition-all"
                  >
                    {tag.label}
                  </Link>
                ))}
              </div>

            </div>

            <SearchResults
              searchQuery={searchQuery}
              results={searchResults}
              onClose={() => setShowSearchResults(false)}
              onResultClick={handleSearchResultClick}
              isVisible={showSearchResults && searchQuery.trim().length >= 2}
            />

            {/* Dedicated Trust Footer Bar with generous breathing room */}
            <div className="pt-6 sm:pt-8 mt-8 sm:mt-12 border-t border-black/15 max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 sm:gap-6 text-center">
                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                  <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black shrink-0 font-black" />
                  <span className="text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-wider text-black">Instant Delivery</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                  <CheckBadgeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black shrink-0 font-black" />
                  <span className="text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-wider text-black">100% Verified Code</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                  <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black shrink-0 font-black" />
                  <span className="text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-wider text-black">Commercial License</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                  <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-black shrink-0 font-black" />
                  <span className="text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-wider text-black">Zero Subscriptions</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. SCROLLING MARQUEE TICKER */}
        <div className="w-full bg-primary border-y-2 sm:border-y-4 border-black py-2.5 sm:py-3.5 overflow-hidden relative z-10 shadow-[0_4px_0px_0px_#000000] sm:shadow-[0_8px_0px_0px_#000000] my-2 sm:-my-4 transform -rotate-0.5 sm:-rotate-1 sm:scale-105">
          <div className="flex whitespace-nowrap animate-marquee-fast">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 sm:gap-8 mx-1.5 sm:mx-4">
                <span className="text-black font-black uppercase text-xs sm:text-xl md:text-2xl tracking-wider sm:tracking-widest" style={{ textShadow: '1px 1px 0px #ffffff' }}>
                  PRO DIGITAL ASSETS
                </span>
                <StarIcon className="h-3 w-3 sm:h-6 sm:w-6 text-black" />
                <span className="text-black font-black uppercase text-xs sm:text-xl md:text-2xl tracking-wider sm:tracking-widest" style={{ textShadow: '1px 1px 0px #ffffff' }}>
                  INSTANT SOURCE CODE
                </span>
                <StarIcon className="h-3 w-3 sm:h-6 sm:w-6 text-black" />
                <span className="text-black font-black uppercase text-xs sm:text-xl md:text-2xl tracking-wider sm:tracking-widest" style={{ textShadow: '1px 1px 0px #ffffff' }}>
                  PERPETUAL LICENSE
                </span>
                <StarIcon className="h-3 w-3 sm:h-6 sm:w-6 text-black" />
                <span className="text-black font-black uppercase text-xs sm:text-xl md:text-2xl tracking-wider sm:tracking-widest" style={{ textShadow: '1px 1px 0px #ffffff' }}>
                  AUTOMATION SUITES
                </span>
                <StarIcon className="h-3 w-3 sm:h-6 sm:w-6 text-black" />
              </div>
            ))}
          </div>
        </div>

        {/* 3. TRENDING DIGITAL ASSETS (MARKETPLACE SHELF) */}
        <section className="container pt-2 sm:pt-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 sm:mb-8 gap-3 sm:gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-black text-primary font-bold uppercase tracking-wider text-[9px] sm:text-xs border border-black mb-1.5 sm:mb-2">
                <FireIcon className="h-3 w-3 sm:h-4 sm:w-4" /> High Demand
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black" style={{ textShadow: '2px 2px 0px #9FE240' }}>
                Trending <span className="bg-black text-primary px-1.5 sm:px-2">Assets</span>
              </h2>
              <p className="text-black font-bold text-xs sm:text-sm mt-1.5 border-l-2 sm:border-l-4 border-primary pl-2.5 sm:pl-3">
                Top-rated source code repositories and automation toolkits this week.
              </p>
            </div>

            {/* Category Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs font-black uppercase border-2 border-black shadow-[2px_2px_0px_0px_#000000] whitespace-nowrap transition-all ${
                  activeTab === 'all' ? 'bg-primary text-black' : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                All Trending
              </button>
              <button
                onClick={() => setActiveTab('dev')}
                className={`px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs font-black uppercase border-2 border-black shadow-[2px_2px_0px_0px_#000000] whitespace-nowrap transition-all ${
                  activeTab === 'dev' ? 'bg-primary text-black' : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                Automation & Code
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs font-black uppercase border-2 border-black shadow-[2px_2px_0px_0px_#000000] whitespace-nowrap transition-all ${
                  activeTab === 'security' ? 'bg-primary text-black' : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                Cyber & SOC
              </button>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {displayedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {/* Bottom View All Link */}
          <div className="text-center mt-6 sm:mt-10">
            <Link 
              to="/products" 
              className="inline-flex items-center justify-center gap-2 btn btn-primary w-full sm:w-auto text-xs sm:text-base px-6 sm:px-8 py-3 sm:py-3.5 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[4px_4px_0px_0px_#000000]"
            >
              <span>View All Marketplace Assets</span>
              <ArrowRightIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5 font-bold" />
            </Link>
          </div>
        </section>

        {/* 4. SPOTLIGHT MEGA ASSET PACK */}
        <section className="container">
          <div className="border-2 sm:border-4 border-black bg-white p-5 sm:p-10 lg:p-14 shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000] relative overflow-hidden">
            <div className="grid lg:grid-cols-12 gap-6 sm:gap-10 items-center relative z-10">
              
              <div className="lg:col-span-7 space-y-4 sm:space-y-6">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3.5 sm:py-1.5 border-2 border-black bg-primary text-black font-black uppercase tracking-wider text-[10px] sm:text-xs shadow-[2px_2px_0px_0px_#000000]">
                  <StarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span>Featured Mega Bundle Pack</span>
                </div>

                <h2 
                  className="text-2xl sm:text-3xl lg:text-5xl font-black uppercase tracking-tight text-black leading-tight"
                  style={{ textShadow: '2px 2px 0px #9FE240' }}
                >
                  The Hackers Library <span className="bg-black text-primary px-1.5 sm:px-2">Vault</span>
                </h2>

                <p className="text-black text-xs sm:text-base font-medium border-l-3 sm:border-l-4 border-primary pl-3 py-1.5 sm:py-2 bg-gray-100 leading-relaxed">
                  Our flagship digital asset super-pack for security engineers and developers. Includes 1,000+ scripts, live lab environments, exploitation frameworks, and enterprise certification prep kits.
                </p>

                {/* Key Asset Deliverables */}
                <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-4 font-bold text-xs sm:text-sm text-black">
                  <div className="flex items-center gap-2.5 bg-secondary p-2.5 sm:p-3 border border-black sm:border-2 shadow-[2px_2px_0px_0px_#000000]">
                    <div className="bg-primary p-1.5 border border-black shrink-0">
                      <ShieldCheckIcon className="h-4 w-4 sm:h-4 sm:w-4 text-black" />
                    </div>
                    <span>Full source code & frameworks</span>
                  </div>

                  <div className="flex items-center gap-2.5 bg-secondary p-2.5 sm:p-3 border border-black sm:border-2 shadow-[2px_2px_0px_0px_#000000]">
                    <div className="bg-primary p-1.5 border border-black shrink-0">
                      <ArrowDownTrayIcon className="h-4 w-4 sm:h-4 sm:w-4 text-black" />
                    </div>
                    <span>Direct cloud drive access</span>
                  </div>

                  <div className="flex items-center gap-2.5 bg-secondary p-2.5 sm:p-3 border border-black sm:border-2 shadow-[2px_2px_0px_0px_#000000]">
                    <div className="bg-primary p-1.5 border border-black shrink-0">
                      <TagIcon className="h-4 w-4 sm:h-4 sm:w-4 text-black" />
                    </div>
                    <span>Unlimited commercial rights</span>
                  </div>

                  <div className="flex items-center gap-2.5 bg-secondary p-2.5 sm:p-3 border border-black sm:border-2 shadow-[2px_2px_0px_0px_#000000]">
                    <div className="bg-primary p-1.5 border border-black shrink-0">
                      <CheckBadgeIcon className="h-4 w-4 sm:h-4 sm:w-4 text-black" />
                    </div>
                    <span>Lifetime updates & releases</span>
                  </div>
                </div>

                <div className="pt-2">
                  <Link
                    to="/digital-bundle/50"
                    className="inline-flex items-center justify-center gap-2 btn btn-primary w-full sm:w-auto text-xs sm:text-base lg:text-lg px-6 sm:px-8 py-3 sm:py-4 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[4px_4px_0px_0px_#000000]"
                  >
                    <span>Inspect Mega Bundle</span>
                    <ArrowRightIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5 font-bold" />
                  </Link>
                </div>
              </div>

              {/* Banner Visual */}
              <div className="lg:col-span-5">
                <div className="relative border-2 sm:border-4 border-black bg-secondary p-1.5 sm:p-2 shadow-[4px_4px_0px_0px_#000000] sm:shadow-[8px_8px_0px_0px_#000000]">
                  <img
                    src="/images/products/security/hackerofferbanner.jpeg"
                    alt="Hackers Library Mega Bundle"
                    className="w-full h-auto object-cover border border-black filter contrast-125"
                  />
                  <div className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 bg-primary border border-black text-black font-black uppercase text-[9px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1 shadow-[2px_2px_0px_0px_#000000]">
                    COMPLETE ASSET PACK
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* 5. MARKETPLACE CATEGORIES MATRIX */}
        <section className="container">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 sm:mb-8 gap-3 sm:gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-black text-primary font-bold uppercase tracking-wider text-[9px] sm:text-xs border border-black mb-1.5 sm:mb-2">
                <Squares2X2Icon className="h-3 w-3 sm:h-4 sm:w-4" /> Browse By Asset Type
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black" style={{ textShadow: '2px 2px 0px #9FE240' }}>
                Marketplace <span className="bg-black text-primary px-1.5 sm:px-2">Categories</span>
              </h2>
              <p className="text-black font-bold text-xs sm:text-sm mt-1.5 border-l-2 sm:border-l-4 border-primary pl-2.5 sm:pl-3">
                Curated production-grade source code, scripts, and frameworks.
              </p>
            </div>
            <Link 
              to="/products" 
              className="inline-flex items-center gap-1.5 text-black font-black uppercase text-xs sm:text-sm border-2 border-black bg-white px-3 sm:px-4 py-1.5 sm:py-2 shadow-[2px_2px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000] hover:bg-primary hover:-translate-y-0.5 transition-all self-start md:self-auto"
            >
              <span>All 50+ Assets</span>
              <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6">
            {assetCategories.map((cat, idx) => {
              const IconComponent = cat.icon;
              return (
                <Link
                  key={idx}
                  to={cat.link}
                  className="bg-white border-2 sm:border-4 border-black p-3.5 sm:p-6 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:shadow-[5px_5px_0px_0px_#000000] sm:hover:shadow-[8px_8px_0px_0px_#000000] hover:-translate-y-0.5 transition-all flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2.5 sm:mb-4">
                      <div className="bg-primary border border-black sm:border-2 p-1.5 sm:p-3 shadow-[1.5px_1.5px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000] group-hover:bg-black group-hover:text-primary transition-colors">
                        <IconComponent className="h-4 w-4 sm:h-7 sm:w-7 text-black group-hover:text-primary" />
                      </div>
                      <span className="text-[9px] sm:text-xs font-black uppercase px-1.5 sm:px-2 py-0.5 bg-black text-primary border border-black">
                        {cat.tag}
                      </span>
                    </div>

                    <h3 className="text-sm sm:text-2xl font-black uppercase tracking-tight text-black mb-1 group-hover:text-primary-dark transition-colors leading-snug">
                      {cat.title}
                    </h3>

                    <p className="text-[9px] sm:text-xs font-black text-primary-dark bg-secondary/80 inline-block px-1.5 sm:px-2 py-0.5 border border-black mb-1.5 sm:mb-3">
                      {cat.count}
                    </p>

                    <p className="hidden sm:block text-gray-800 text-xs sm:text-sm font-medium line-clamp-2">
                      {cat.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2 sm:pt-4 mt-2.5 sm:mt-6 border-t sm:border-t-2 border-black text-[11px] sm:text-sm font-black uppercase text-black group-hover:text-primary transition-colors">
                    <span className="truncate">Explore</span>
                    <ArrowRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* 6. MARKETPLACE BUYER GUARANTEE */}
        <section className="container">
          <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-12">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-black text-primary font-bold uppercase tracking-wider text-[9px] sm:text-xs border border-black mb-1.5 sm:mb-2">
              <CheckBadgeIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Marketplace Standards
            </div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-black" style={{ textShadow: '2px 2px 0px #9FE240' }}>
              Built For <span className="bg-black text-primary px-1.5 sm:px-2">Builders</span>
            </h2>
            <p className="text-black font-bold text-xs sm:text-sm mt-1.5">
              Every digital asset in the Pingfence marketplace meets uncompromising engineering standards.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-6">
            
            <div className="bg-white border-2 sm:border-4 border-black p-3.5 sm:p-6 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-0.5 transition-all flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 sm:w-14 sm:h-14 bg-primary border sm:border-2 border-black flex items-center justify-center mb-2.5 sm:mb-4 shadow-[1.5px_1.5px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000]">
                  <ArrowDownTrayIcon className="h-4 w-4 sm:h-7 sm:w-7 text-black font-black" />
                </div>
                <h3 className="text-xs sm:text-xl font-black uppercase tracking-tight text-black mb-1 leading-tight">Instant Delivery</h3>
                <p className="text-[10px] sm:text-sm font-medium text-gray-700 leading-snug line-clamp-3 sm:line-clamp-none">
                  Unlock direct cloud links immediately upon purchase. Download ZIPs instantly anytime.
                </p>
              </div>
            </div>

            <div className="bg-white border-2 sm:border-4 border-black p-3.5 sm:p-6 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-0.5 transition-all flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 sm:w-14 sm:h-14 bg-primary border sm:border-2 border-black flex items-center justify-center mb-2.5 sm:mb-4 shadow-[1.5px_1.5px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000]">
                  <CodeBracketIcon className="h-4 w-4 sm:h-7 sm:w-7 text-black font-black" />
                </div>
                <h3 className="text-xs sm:text-xl font-black uppercase tracking-tight text-black mb-1 leading-tight">Production Code</h3>
                <p className="text-[10px] sm:text-sm font-medium text-gray-700 leading-snug line-clamp-3 sm:line-clamp-none">
                  Every line of source code is formatted, tested, and verified clean and secure.
                </p>
              </div>
            </div>

            <div className="bg-white border-2 sm:border-4 border-black p-3.5 sm:p-6 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-0.5 transition-all flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 sm:w-14 sm:h-14 bg-primary border sm:border-2 border-black flex items-center justify-center mb-2.5 sm:mb-4 shadow-[1.5px_1.5px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000]">
                  <BriefcaseIcon className="h-4 w-4 sm:h-7 sm:w-7 text-black font-black" />
                </div>
                <h3 className="text-xs sm:text-xl font-black uppercase tracking-tight text-black mb-1 leading-tight">Commercial License</h3>
                <p className="text-[10px] sm:text-sm font-medium text-gray-700 leading-snug line-clamp-3 sm:line-clamp-none">
                  Use digital assets in client projects and internal apps with zero royalty fees.
                </p>
              </div>
            </div>

            <div className="bg-white border-2 sm:border-4 border-black p-3.5 sm:p-6 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:-translate-y-0.5 transition-all flex flex-col justify-between">
              <div>
                <div className="w-9 h-9 sm:w-14 sm:h-14 bg-primary border sm:border-2 border-black flex items-center justify-center mb-2.5 sm:mb-4 shadow-[1.5px_1.5px_0px_0px_#000000] sm:shadow-[3px_3px_0px_0px_#000000]">
                  <SparklesIcon className="h-4 w-4 sm:h-7 sm:w-7 text-black font-black" />
                </div>
                <h3 className="text-xs sm:text-xl font-black uppercase tracking-tight text-black mb-1 leading-tight">Perpetual Access</h3>
                <p className="text-[10px] sm:text-sm font-medium text-gray-700 leading-snug line-clamp-3 sm:line-clamp-none">
                  No recurring fees or monthly lock-in. Pay once, keep lifetime access to all updates.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* 7. SPECIAL OFFERS / MARKETPLACE PASS BANNER */}
        <section className="container pb-8 sm:pb-20">
          <div className="bg-primary border-2 sm:border-4 border-black p-5 sm:p-10 lg:p-14 shadow-[6px_6px_0px_0px_#000000] sm:shadow-[12px_12px_0px_0px_#000000] relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8">
            <div className="absolute -top-10 -right-10 w-32 sm:w-40 h-32 sm:h-40 bg-white border-4 border-black rounded-full mix-blend-overlay opacity-40"></div>
            <div className="absolute -bottom-10 -left-10 w-24 sm:w-32 h-24 sm:h-32 bg-black border-4 border-white rotate-12 opacity-10"></div>

            <div className="relative z-10 md:w-2/3">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-black text-primary font-bold uppercase tracking-widest text-[9px] sm:text-xs border border-black mb-2">
                <SparklesIcon className="h-3 w-3 sm:h-4 sm:w-4" /> Marketplace Discount
              </div>
              <h2 className="text-xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight text-black mb-2 sm:mb-3" style={{ textShadow: '2px 2px 0px #ffffff' }}>
                Save 50% On All Digital Asset Bundles!
              </h2>
              <p className="text-black font-bold text-xs sm:text-base border-l-2 sm:border-l-4 border-black pl-2.5 bg-white/40 p-1.5 sm:p-2">
                Use code{' '}
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText('HACKER50');
                    toast.success('Code HACKER50 Copied! 50% OFF at Checkout', {
                      icon: '🎁',
                      style: {
                        background: '#9FE240',
                        color: '#000000',
                        border: '2px solid #000000',
                        boxShadow: '4px 4px 0px 0px #000000',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }
                    });
                  }}
                  title="Click to copy promo code"
                  className="bg-white px-2 py-0.5 border border-black font-black mx-0.5 hover:bg-black hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1 active:scale-95"
                >
                  <span>HACKER50</span>
                  <span className="text-[10px] uppercase text-gray-600 font-bold">📋 Copy</span>
                </button>{' '}
                at checkout for instant half-price discount.
              </p>
            </div>

            <div className="relative z-10 w-full md:w-1/3 text-center md:text-right">
              <Link 
                to="/digital-bundles" 
                className="btn bg-white text-black text-xs sm:text-lg font-black w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 border-2 sm:border-4 border-black shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:shadow-[5px_5px_0px_0px_#000000] hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              >
                <span>Claim Offer</span>
                <ArrowRightIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5 font-black" />
              </Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Home;